import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { ProgressBar } from './components/ui/ProgressBar';
import { SlideTitle } from './components/SlideTitle';
import { SlideChapterIntro } from './components/SlideChapterIntro';
import { SlideLifecycle } from './components/SlideLifecycle';
import { SlideMarketSize } from './components/SlideMarketSize';
import { SlideProblem } from './components/SlideProblem';
import { SlideAIOpportunity } from './components/SlideAIOpportunity';
import { SlideCalculator } from './components/SlideCalculator';
import { SlideAIEconomics } from './components/SlideAIEconomics';
import { SlideNewCategory } from './components/SlideNewCategory';
import { SlideStrategy } from './components/SlideStrategy';
import { SlideWhatSnakkerDoes } from './components/SlideWhatSnakkerDoes';
import { SlideSnakJourney } from './components/SlideSnakJourney';
import { SlidePlatformArchitecture } from './components/SlidePlatformArchitecture';
import { SlideMoat } from './components/SlideMoat';
import { SlideTraction } from './components/SlideTraction';
import { SlideSalesFocus } from './components/SlideSalesFocus';
import { SlideFastCalculator } from './components/SlideFastCalculator';
import { SlideFinancialProjections } from './components/SlideFinancialProjections';
import { SlideTheRaise } from './components/SlideTheRaise';
import { SlideCTA } from './components/SlideCTA';
import { ExecutiveSummary } from './components/ExecutiveSummary';
import { LanguageProvider, useLanguage } from './components/LanguageContext';
function SnakkerApp() {
  const {
    lang,
    setLang,
    t
  } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const totalSlides = 23;
  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setDirection(1);
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide, totalSlides]);
  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setDirection(-1);
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);
  const jumpToSlide = useCallback((index: number) => {
    if (index >= 0 && index < totalSlides && index !== currentSlide) {
      setDirection(index > currentSlide ? 1 : -1);
      setCurrentSlide(index);
    }
  }, [currentSlide, totalSlides]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') nextSlide();else if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  const renderSlide = () => {
    switch (currentSlide) {
      case 0:
        return <SlideTitle onContinue={nextSlide} jumpToSlide={jumpToSlide} data-id="element-0" />;
      case 1:
        return <SlideChapterIntro onContinue={nextSlide} number="01" name={t('The Opportunity', 'Muligheden')} description={t('Why legacy pharma revenue is the most under-optimized segment in healthcare — and why even small improvements compound into enormous value.', 'Hvorfor legacy pharma-omsætning er det mest underoptimerede segment i sundhedssektoren — og hvorfor selv små forbedringer akkumuleres til enorm værdi.')} topics={[t('The pharma lifecycle', 'Pharma-livscyklussen'), t('Market size', 'Markedsstørrelse'), t('The problem', 'Problemet'), t('The unlocked value', 'Den frigjorte værdi')]} nextLabel={t('Lifecycle', 'Livscyklus')} data-id="element-1" />;
      case 2:
        return <SlideLifecycle onContinue={nextSlide} data-id="element-2" />;
      case 3:
        return <SlideMarketSize onContinue={nextSlide} data-id="element-3" />;
      case 4:
        return <SlideProblem onContinue={nextSlide} data-id="element-4" />;
      case 5:
        return <SlideCalculator onContinue={nextSlide} data-id="element-5" />;
      case 6:
        return <SlideChapterIntro onContinue={nextSlide} number="02" name={t('The Solution', 'Løsningen')} description={t('A new operating model for pharma campaigns — built on AI execution, behavioral data, and a Snak format that fits every stage of the HCP journey.', 'En ny driftsmodel for pharma-kampagner — bygget på AI-eksekvering, adfærdsdata og et Snak-format, der passer til alle faser af HCP-rejsen.')} topics={[t('AI economics', 'AI-økonomi'), t('A new category', 'En ny kategori'), t('Three levers', 'Tre håndtag'), t('Operating model', 'Driftsmodel'), t('What Snakker does', 'Hvad Snakker gør'), t('Customer journey', 'Kunderejse'), t('Platform architecture', 'Platformarkitektur')]} nextLabel={t('AI Economics', 'AI-økonomi')} data-id="element-6" />;
      case 7:
        return <SlideAIEconomics onContinue={nextSlide} data-id="element-7" />;
      case 8:
        return <SlideNewCategory onContinue={nextSlide} data-id="element-8" />;
      case 9:
        return <SlideAIOpportunity onContinue={nextSlide} data-id="element-9" />;
      case 10:
        return <SlideStrategy onContinue={nextSlide} data-id="element-10" />;
      case 11:
        return <SlideWhatSnakkerDoes onContinue={nextSlide} data-id="element-11" />;
      case 12:
        return <SlideSnakJourney onContinue={nextSlide} data-id="element-12" />;
      case 13:
        return <SlidePlatformArchitecture onContinue={nextSlide} data-id="element-13" />;
      case 14:
        return <SlideChapterIntro onContinue={nextSlide} number="03" name={t('The Business', 'Forretningen')} description={t("What defends the model, what's already in the pipeline, and the strategic reason behind the raise.", 'Hvad der forsvarer modellen, hvad der allerede er i pipelinen, og den strategiske årsag bag kapitalrejsningen.')} topics={[t('The moat', 'Voldgraven'), t('Traction', 'Traktion'), t("Why we're raising", 'Hvorfor vi rejser kapital')]} nextLabel={t('The Moat', 'Voldgraven')} data-id="element-14" />;
      case 15:
        return <SlideMoat onContinue={nextSlide} data-id="element-15" />;
      case 16:
        return <SlideTraction onContinue={nextSlide} data-id="element-16" />;
      case 17:
        return <SlideSalesFocus onContinue={nextSlide} data-id="element-17" />;
      case 18:
        return <SlideChapterIntro onContinue={nextSlide} number="04" name={t('The Ask', 'Investeringen')} description={t('Unit economics, financial projections, the raise itself — and what it makes possible.', 'Enhedsøkonomi, finansielle fremskrivninger, selve kapitalrejsningen — og hvad den muliggør.')} topics={[t('Cost calculator', 'Omkostningsberegner'), t('Financial projections', 'Finansielle fremskrivninger'), t('The raise', 'Kapitalrejsningen'), t('Closing', 'Afslutning')]} nextLabel={t('Cost Calculator', 'Omkostningsberegner')} data-id="element-18" />;
      case 19:
        return <SlideFastCalculator onContinue={nextSlide} data-id="element-19" />;
      case 20:
        return <SlideFinancialProjections onContinue={nextSlide} data-id="element-20" />;
      case 21:
        return <SlideTheRaise onContinue={nextSlide} data-id="element-21" />;
      case 22:
        return <SlideCTA onContinue={nextSlide} data-id="element-22" />;
      default:
        return null;
    }
  };
  return <div className="w-full h-screen bg-navy-800 overflow-hidden relative font-sans text-slate-900" data-id="element-23">
      <ProgressBar current={currentSlide} total={totalSlides} onBack={prevSlide} onSkip={nextSlide} data-id="element-24" />

      {/* Language Switcher */}
      <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-navy-900/60 backdrop-blur-md px-2.5 py-1.5 rounded-full border border-white/10 shadow-lg" data-id="element-25">
        <button onClick={() => setLang('en')} className={`transition-all rounded-full overflow-hidden flex items-center justify-center ${lang === 'en' ? 'opacity-100 scale-110 ring-2 ring-teal-400' : 'opacity-50 hover:opacity-90'}`} title="English" aria-label="English" data-id="element-26">
          {/* UK flag SVG */}
          <svg width="22" height="22" viewBox="0 0 60 60" className="block" data-id="element-27">
            <clipPath id="ukcircle" data-id="element-28">
              <circle cx="30" cy="30" r="30" data-id="element-29" />
            </clipPath>
            <g clipPath="url(#ukcircle)" data-id="element-30">
              <rect width="60" height="60" fill="#012169" data-id="element-31" />
              <path d="M0,0 L60,60 M60,0 L0,60" stroke="#fff" strokeWidth="8" data-id="element-32" />
              <path d="M0,0 L60,60 M60,0 L0,60" stroke="#C8102E" strokeWidth="4" data-id="element-33" />
              <path d="M30,0 V60 M0,30 H60" stroke="#fff" strokeWidth="12" data-id="element-34" />
              <path d="M30,0 V60 M0,30 H60" stroke="#C8102E" strokeWidth="6" data-id="element-35" />
            </g>
          </svg>
        </button>
        <div className="w-px h-4 bg-white/20" data-id="element-36" />
        <button onClick={() => setLang('da')} className={`transition-all rounded-full overflow-hidden flex items-center justify-center ${lang === 'da' ? 'opacity-100 scale-110 ring-2 ring-teal-400' : 'opacity-50 hover:opacity-90'}`} title="Dansk" aria-label="Dansk" data-id="element-37">
          {/* Danish flag SVG */}
          <svg width="22" height="22" viewBox="0 0 37 28" className="block" data-id="element-38">
            <clipPath id="dkcircle" data-id="element-39">
              <circle cx="18.5" cy="14" r="14" data-id="element-40" />
            </clipPath>
            <g clipPath="url(#dkcircle)" data-id="element-41">
              <rect width="37" height="28" fill="#C8102E" data-id="element-42" />
              <rect x="12" y="0" width="4" height="28" fill="#fff" data-id="element-43" />
              <rect x="0" y="12" width="37" height="4" fill="#fff" data-id="element-44" />
            </g>
          </svg>
        </button>
      </div>

      <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-45">
        <motion.div key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
        x: {
          type: 'spring',
          stiffness: 300,
          damping: 30
        },
        opacity: {
          duration: 0.2
        }
      }} className="absolute inset-0 w-full h-full" data-id="element-46">
          {renderSlide()}
        </motion.div>
      </AnimatePresence>

      {/* Floating Executive Summary button */}
      <button onClick={() => setSummaryOpen(true)} className="fixed bottom-6 left-6 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#6C2BD9] text-sm font-semibold shadow-lg shadow-[#6C2BD9]/30 hover:bg-[#5A23B8] hover:shadow-xl hover:shadow-[#6C2BD9]/40 hover:-translate-y-0.5 transition-all" aria-label="Open executive summary" style={{
      color: '#ffffff'
    }} data-id="element-47">
        <Sparkles className="w-4 h-4" style={{
        color: '#ffffff'
      }} data-id="element-48" />
        <span style={{
        color: '#ffffff'
      }} data-id="element-49">
          {t('Executive Summary', 'Ledelsesresumé')}
        </span>
      </button>

      <ExecutiveSummary open={summaryOpen} onClose={() => setSummaryOpen(false)} data-id="element-50" />
    </div>;
}
export function App() {
  return <LanguageProvider data-id="element-51">
      <SnakkerApp data-id="element-52" />
    </LanguageProvider>;
}